<div class="page-container">
    <!-- Header Toolbar -->
    <mat-toolbar color="primary" class="toolbar-fixed">
        <button mat-icon-button (click)="onBack()" [attr.aria-label]="'Back'">
            <mat-icon>arrow_back</mat-icon>
        </button>
        <span class="toolbar-title">
            <mat-icon class="mr-2">videocam</mat-icon>
            @if (!isEditingName()) {
            Device Group - {{ deviceGroup()?.groupName }}
            <button mat-icon-button (click)="startEditingName()" [attr.aria-label]="'Edit name'" class="ml-1">
                <mat-icon>edit</mat-icon>
            </button>
            }
            @if (isEditingName()) {
            <mat-form-field appearance="fill" class="name-edit-field">
                <input matInput [(ngModel)]="editingGroupName" (keyup.enter)="saveGroupName()"
                    (keyup.escape)="cancelEditName()">
            </mat-form-field>
            <button mat-icon-button color="accent" (click)="saveGroupName()" [disabled]="isProcessing()"
                [attr.aria-label]="'Save name'">
                <mat-icon>check</mat-icon>
            </button>
            <button mat-icon-button (click)="cancelEditName()" [attr.aria-label]="'Cancel edit'">
                <mat-icon>close</mat-icon>
            </button>
            }
        </span>
        <span class="spacer"></span>
    </mat-toolbar>

    <!-- Loading Bar -->
    @if (isLoading()) {
    <mat-progress-bar mode="indeterminate" class="progress-bar-fixed"></mat-progress-bar>
    }

    <!-- Main Content with Tabs -->
    <div class="page-content p-6">
        @if (!isLoading()) {
        <!-- Direction: which reads this group serves (a multi-direction camera in several groups) -->
        <div class="flex items-center gap-3 mb-4">
            <label for="group-direction" class="text-sm font-medium text-slate-900">Direction</label>
            <select id="group-direction"
                class="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                [disabled]="isProcessing()" [value]="currentDirection()"
                (change)="onDirectionChange($any($event.target).value)">
                @for (option of directionOptions; track option.value) {
                <option [value]="option.value" [selected]="option.value === currentDirection()">{{ option.label }}</option>
                }
            </select>
        </div>

        <mat-tab-group class="device-group-tabs" [selectedIndex]="selectedTabIndex()"
            (selectedTabChange)="selectedTabIndex.set($event.index)">
            <!-- Cameras Tab -->
            <mat-tab label="Cameras" [attr.aria-label]="'Cameras Tab'">
                <ng-template mat-tab-label>
                    <mat-icon class="tab-icon">videocam</mat-icon>
                    <span>Cameras ({{ cameras().length }})</span>
                </ng-template>

                <div class="tab-content">
                    <!-- Create / Edit Camera Form -->
                    @if (showCreateForm()) {
                    <mat-card class="create-form-card mb-6">
                        <mat-card-header>
                            <mat-card-title>{{ editingCamera() ? 'Edit Camera' : 'Add New Camera' }}</mat-card-title>
                        </mat-card-header>
                        <mat-card-content>
                            <app-create-camera-form [isProcessing]="isProcessing()" [camera]="editingCamera()"
                                [mode]="editingCamera() ? 'edit' : 'create'" (createCamera)="onCreateCamera($event)"
                                (cancel)="closeCameraForm()">
                            </app-create-camera-form>
                        </mat-card-content>
                    </mat-card>
                    }

                    @if (!showCreateForm()) {
                    <div class="flex flex-wrap items-center justify-between gap-3 mb-5">
                        <div>
                            <h2 class="text-base font-bold text-slate-900">ANPR / LPR Cameras</h2>
                            <p class="text-xs text-slate-500 mt-0.5">License plate recognition cameras attached to this device group.</p>
                        </div>
                        <div class="flex items-center gap-2">
                            <button mat-flat-button color="primary" (click)="openCreateCameraForm()"
                                [disabled]="isLoading()">
                                <mat-icon>add</mat-icon>
                                Add Camera
                            </button>
                            <button mat-stroked-button color="primary" (click)="openAssignExistingCamera()"
                                [disabled]="isLoading() || !infrastructureId()">
                                <mat-icon>link</mat-icon>
                                Assign Existing Camera
                            </button>
                        </div>
                    </div>
                    }

                    @if (cameras().length === 0 && !showCreateForm()) {
                    <div class="empty-state">
                        <mat-icon class="empty-state-icon">videocam_off</mat-icon>
                        <h2>No Cameras Configured</h2>
                        <p>No cameras configured for this device group yet.</p>
                        <button mat-flat-button color="primary" (click)="openCreateCameraForm()">
                            <mat-icon>add</mat-icon>
                            Add First Camera
                        </button>
                    </div>
                    }

                    @if (cameras().length > 0) {
                    <div class="cameras-grid">
                        @for (camera of cameras(); track camera.id) {
                        <app-zone-camera [camera]="camera" [parkingSpaceId]="parkingSpaceId()"
                            [class.highlighted-camera]="isHighlightedCamera(camera.id || '')"
                            [isProcessing]="isProcessing()" [isConnecting]="isCameraConnecting(camera.id || '')"
                            [isStartingLPR]="isCameraStartingLPR(camera.id || '')" (removeCamera)="removeCamera($event)"
                            (editCamera)="openEditCameraForm($event)" (connectCamera)="connectCamera($event)"
                            (startCameraLPRListener)="startCameraLPRListener($event)">
                        </app-zone-camera>
                        }
                    </div>
                    }
                </div>
            </mat-tab>

            <!-- Devices Tab -->
            <mat-tab label="Devices" [attr.aria-label]="'Devices Tab'">
                <ng-template mat-tab-label>
                    <mat-icon class="tab-icon">devices</mat-icon>
                    <span>Devices ({{ devices().length }})</span>
                </ng-template>

                <div class="tab-content">
                    <app-zone-list-devices [devices]="devices()" [isLoading]="isLoading()">
                    </app-zone-list-devices>
                </div>
            </mat-tab>

            <!-- Barrier Tab -->
            <mat-tab label="Barrier" [attr.aria-label]="'Barrier Tab'">
                <ng-template mat-tab-label>
                    <mat-icon class="tab-icon">door_front</mat-icon>
                    <span>Barrier</span>
                </ng-template>

                <div class="tab-content">
                    @if (showCreateBarrierForm()) {
                    <mat-card class="create-form-card mb-6">
                        <mat-card-header>
                            <mat-card-title>{{ editingBarrier() ? 'Edit Barrier' : 'Add Barrier' }}</mat-card-title>
                        </mat-card-header>
                        <mat-card-content>
                            <app-create-barrier-form [isProcessing]="isProcessing()" [parkingSpaceId]="parkingSpaceId()"
                                [barrier]="editingBarrier()" [mode]="editingBarrier() ? 'edit' : 'create'"
                                (createBarrier)="onCreateBarrier($event)" (cancel)="closeBarrierForm()">
                            </app-create-barrier-form>
                        </mat-card-content>
                    </mat-card>
                    }

                    @if (!showCreateBarrierForm() && !barrier()) {
                    <div class="flex gap-2 mb-4">
                        <button mat-raised-button color="accent" (click)="toggleCreateBarrierForm()">
                            <mat-icon>add</mat-icon>
                            Add Barrier
                        </button>
                        <button mat-stroked-button color="accent" (click)="openAssignExistingBarrier()"
                            [disabled]="!infrastructureId()">
                            <mat-icon>link</mat-icon>
                            Assign Existing Barrier
                        </button>
                    </div>
                    }

                    @if (!barrier() && !showCreateBarrierForm()) {
                    <div class="empty-state">
                        <mat-icon class="empty-state-icon">door_front</mat-icon>
                        <h2>No Barrier</h2>
                        <p>This device group can have one barrier. Add a barrier to enable commands.</p>
                        <button mat-raised-button color="primary" (click)="toggleCreateBarrierForm()">
                            <mat-icon>add</mat-icon>
                            Create Barrier
                        </button>
                    </div>
                    }

                    @if (barrier()) {
                    <div class="grid-container">
                        <app-zone-barrier [barrier]="barrier()!" [isProcessing]="isProcessing()"
                            (editBarrier)="openEditBarrierForm($event)" (deleteBarrier)="deleteBarrier($event)"
                            (unassignBarrier)="unassignBarrier($event)">
                        </app-zone-barrier>
                    </div>
                    }
                </div>
            </mat-tab>

            <!-- Displays Tab -->
            <mat-tab label="Displays" [attr.aria-label]="'Displays Tab'">
                <ng-template mat-tab-label>
                    <mat-icon class="tab-icon">tv</mat-icon>
                    <span>Displays ({{ displays().length }})</span>
                </ng-template>

                <div class="tab-content">
                    @if (showCreateDisplayForm()) {
                    <mat-card class="create-form-card mb-6">
                        <mat-card-header>
                            <mat-card-title>{{ editingDisplay() ? 'Edit Display' : 'Add Display' }}</mat-card-title>
                        </mat-card-header>
                        <mat-card-content>
                            <app-create-display-form [isProcessing]="isProcessing()" [display]="editingDisplay()"
                                [mode]="editingDisplay() ? 'edit' : 'create'" [parkingSpaceId]="parkingSpaceId()"
                                [deviceGroupId]="deviceGroup()?.id ?? null" (saveDisplay)="onSaveDisplay($event)"
                                (cancel)="closeDisplayForm()">
                            </app-create-display-form>
                        </mat-card-content>
                    </mat-card>
                    }

                    @if (!showCreateDisplayForm()) {
                    <div class="flex gap-2 mb-4">
                        <button mat-raised-button color="accent" (click)="openCreateDisplayForm()">
                            <mat-icon>add</mat-icon>
                            Add Display
                        </button>
                        <button mat-stroked-button color="accent" (click)="openAssignExistingDisplay()"
                            [disabled]="!infrastructureId()">
                            <mat-icon>link</mat-icon>
                            Assign Existing Display
                        </button>
                    </div>
                    }

                    @if (displays().length === 0 && !showCreateDisplayForm()) {
                    <div class="empty-state">
                        <mat-icon class="empty-state-icon">tv_off</mat-icon>
                        <h2>No Displays</h2>
                        <p>No displays configured for this device group yet.</p>
                        <button mat-raised-button color="primary" (click)="openCreateDisplayForm()">
                            <mat-icon>add</mat-icon>
                            Add First Display
                        </button>
                    </div>
                    }

                    @if (displays().length > 0) {
                    <div class="grid-container">
                        @for (display of displays(); track display.id) {
                        <app-zone-display [display]="display" [isProcessing]="isProcessing()"
                            (editDisplay)="openEditDisplayForm($event)" (deleteDisplay)="deleteDisplay($event)"
                            (testHeartbeat)="onTestDisplayHeartbeat($event)"
                            (unassignDisplay)="unassignDisplay($event)">
                        </app-zone-display>
                        }
                    </div>
                    }
                </div>
            </mat-tab>

            <!-- Payment Terminals Tab -->
            <mat-tab label="Payment Terminals" [attr.aria-label]="'Payment Terminals Tab'">
                <ng-template mat-tab-label>
                    <mat-icon class="tab-icon">point_of_sale</mat-icon>
                    <span>Payment ({{ paymentTerminals().length }})</span>
                </ng-template>

                <div class="tab-content">
                    @if (showCreatePaymentTerminalForm()) {
                    <mat-card class="create-form-card mb-6">
                        <mat-card-header>
                            <mat-card-title>Add Payment Terminal</mat-card-title>
                        </mat-card-header>
                        <mat-card-content>
                            <app-create-payment-terminal-form [isProcessing]="isProcessing()"
                                [terminal]="editingPaymentTerminal()"
                                (createTerminal)="onCreatePaymentTerminal($event)"
                                (updateTerminal)="onUpdatePaymentTerminal($event)"
                                (cancel)="closePaymentTerminalForm()">
                            </app-create-payment-terminal-form>
                        </mat-card-content>
                    </mat-card>
                    }

                    @if (!showCreatePaymentTerminalForm()) {
                    <div class="flex gap-2 mb-4">
                        <button mat-raised-button color="accent" (click)="openCreatePaymentTerminalForm()"
                            [disabled]="isLoading()">
                            <mat-icon>add</mat-icon>
                            Add Payment Terminal
                        </button>
                    </div>
                    }

                    @if (paymentTerminals().length === 0 && !showCreatePaymentTerminalForm()) {
                    <div class="empty-state">
                        <mat-icon class="empty-state-icon">point_of_sale</mat-icon>
                        <h2>No Payment Terminals</h2>
                        <p>No ZVT card terminals (IM30 / Valina) configured for this device group yet.</p>
                        <button mat-raised-button color="primary" (click)="openCreatePaymentTerminalForm()">
                            <mat-icon>add</mat-icon>
                            Add First Payment Terminal
                        </button>
                    </div>
                    }

                    @if (paymentTerminals().length > 0) {
                    <div class="grid-container">
                        @for (terminal of paymentTerminals(); track terminal.id) {
                        <app-zone-payment-terminal [terminal]="terminal" [isProcessing]="isProcessing()"
                            (deleteTerminal)="deletePaymentTerminal($event)"
                            (editTerminal)="openEditPaymentTerminalForm($event)">
                        </app-zone-payment-terminal>
                        }
                    </div>
                    }
                </div>
            </mat-tab>

            <!-- Detection Points Tab -->
            <mat-tab label="Detection Points" [attr.aria-label]="'Detection Points Tab'">
                <ng-template mat-tab-label>
                    <mat-icon class="tab-icon">location_on</mat-icon>
                    <span>Points ({{ groupDetectionPoints().length }})</span>
                </ng-template>

                <div class="tab-content">
                    <!-- Create Detection Point Form -->
                    @if (showCreateDetectionPointForm()) {
                    <mat-card class="create-form-card mb-6">
                        <mat-card-header>
                            <mat-card-title>Add Detection Point</mat-card-title>
                        </mat-card-header>
                        <mat-card-content>
                            <app-create-detection-point-form [isProcessing]="isProcessing()" [cameras]="cameras()"
                                (createDetectionPoint)="onCreateDetectionPoint($event)"
                                (cancel)="toggleCreateDetectionPointForm()">
                            </app-create-detection-point-form>
                        </mat-card-content>
                    </mat-card>
                    }

                    @if (!showCreateDetectionPointForm() && cameras().length > 0) {
                    <button mat-raised-button color="accent" (click)="toggleCreateDetectionPointForm()" class="mb-4">
                        <mat-icon>add</mat-icon>
                        Add Detection Point
                    </button>
                    }

                    @if (groupDetectionPoints().length === 0 && !showCreateDetectionPointForm()) {
                    <div class="empty-state">
                        <mat-icon class="empty-state-icon">location_off</mat-icon>
                        <h2>No Detection Points</h2>
                        @if (cameras().length === 0) {
                        <p>Please add a camera first to create detection points.</p>
                        }
                        @if (cameras().length > 0) {
                        <p>No detection points configured for this device group.</p>
                        }
                        @if (cameras().length > 0) {
                        <button mat-raised-button color="primary" (click)="toggleCreateDetectionPointForm()">
                            <mat-icon>add</mat-icon>
                            Create First Detection Point
                        </button>
                        }
                    </div>
                    }

                    @if (groupDetectionPoints().length > 0) {
                    <div class="grid-container">
                        @for ( point of groupDetectionPoints(); track point.id) {
                        <app-zone-detection-point [detectionPoint]="point" [cameras]="cameras()" [saving]="isProcessing()"
                            (delete)="deleteDetectionPoint($event)" (navigateToCamera)="navigateToCamera($event)"
                            (save)="saveDetectionPoint($event)">
                        </app-zone-detection-point>
                        }
                    </div>
                    }
                </div>
            </mat-tab>

            <!-- Detection Patterns Tab -->
            <mat-tab label="Detection Patterns" [attr.aria-label]="'Detection Patterns Tab'">
                <ng-template mat-tab-label>
                    <mat-icon class="tab-icon">timeline</mat-icon>
                    <span>Patterns ({{ detectionPatterns().length }})</span>
                </ng-template>

                <div class="tab-content">
                    @if (showCreatePatternForm()) {
                    <mat-card class="create-form-card mb-6">
                        <mat-card-header>
                            <mat-card-title>Create New Pattern</mat-card-title>
                        </mat-card-header>
                        <mat-card-content>
                            <app-create-pattern-form [isProcessing]="isProcessing()"
                                [detectionPoints]="detectionPoints()" [parkingSpaceId]="parkingSpaceId()"
                                (createPattern)="createDetectionPattern($event)" (cancel)="toggleCreatePatternForm()">
                            </app-create-pattern-form>
                        </mat-card-content>
                    </mat-card>
                    }

                    @if (!showCreatePatternForm() && detectionPoints().length > 0) {
                    <button mat-raised-button color="accent" (click)="toggleCreatePatternForm()" class="mb-4">
                        <mat-icon>add</mat-icon>
                        Create Pattern
                    </button>
                    }

                    @if (detectionPatterns().length === 0 && !showCreatePatternForm()) {
                    <div class="empty-state">
                        <mat-icon class="empty-state-icon">timeline</mat-icon>
                        <h2>No Detection Patterns</h2>
                        @if (detectionPoints().length === 0) {
                        <p>Please create detection points first to build a
                            pattern.</p>
                        }
                        @if (detectionPoints().length > 0) {
                        <p>No detection sequence patterns configured for this
                            parking zone.</p>
                        }
                        @if (detectionPoints().length > 0) {
                        <button mat-raised-button color="primary" (click)="toggleCreatePatternForm()">
                            <mat-icon>add</mat-icon>
                            Create First Pattern
                        </button>
                        }
                    </div>
                    }

                    @if (detectionPatterns().length > 0) {
                    <div class="grid-container">
                        @for ( pattern of detectionPatterns(); track pattern.id) {
                        <app-zone-detection-pattern [pattern]="pattern" [parkingSpace]="parkingSpace()"
                            (editPattern)="editDetectionPattern($event)"
                            (deletePattern)="deleteDetectionPattern($event)">
                        </app-zone-detection-pattern>
                        }
                    </div>
                    }
                </div>
            </mat-tab>
        </mat-tab-group>
        }
    </div>
</div>